import React, { useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowUpRight } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import { sendContactEmail } from "../../emailService";

const initialFormData = {
  user_name: "",
  user_phone: "",
  user_email: "",
  user_project: "",
};

function Form() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const form = useRef();

  const handleForm = (event) => {
    setFormData((currentData) => ({
      ...currentData,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await sendContactEmail(form.current);
      toast.success("Your request has been sent successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error("EmailJS submission failed:", error);
      toast.error("We couldn't send your request. Please try again.", {
        position: "top-right",
        autoClose: 2500,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClassName =
    "quote-form-input mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-[#e2bd00] focus:bg-white focus:ring-4 focus:ring-[#FFDB16]/15 sm:mt-2 sm:py-3";

  return (
    <div className="quote-form-card flex h-full min-h-0 w-full flex-col justify-start rounded-[24px] border border-white/70 bg-white p-4 shadow-[0_30px_80px_rgba(0,0,0,0.28)] sm:rounded-[28px] sm:p-5 lg:p-6">
      <ToastContainer newestOnTop closeOnClick pauseOnHover theme="light" />

      <div className="quote-form-intro mb-3 sm:mb-4">
        <div className="quote-form-eyebrow mb-2 hidden items-center gap-2 sm:flex">
          <span className="h-2 w-2 rounded-full bg-[#FFDB16]" />
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            Free project consultation
          </p>
        </div>
        <h3 className="text-2xl font-extrabold tracking-[-0.03em] text-slate-950 sm:text-[1.8rem]">
          Tell us about your idea
        </h3>
        <p className="quote-form-copy mt-1 max-w-xl text-xs leading-5 text-slate-500 sm:mt-1.5 sm:text-sm sm:leading-5">
          Share a few details and our team will get back to you within one business day.
        </p>
      </div>

      <form ref={form} onSubmit={handleSubmit} className="quote-form-fields space-y-2.5 sm:space-y-3">
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
          <label htmlFor="name" className="text-xs font-semibold text-slate-700 sm:text-sm">
            Your name <span className="text-rose-500">*</span>
            <input
              type="text"
              name="user_name"
              id="name"
              autoComplete="name"
              value={formData.user_name}
              onChange={handleForm}
              className={inputClassName}
              placeholder="e.g. Anjali Verma"
              required
            />
          </label>

          <label htmlFor="phone" className="text-xs font-semibold text-slate-700 sm:text-sm">
            Phone number <span className="text-rose-500">*</span>
            <input
              type="tel"
              name="user_phone"
              id="phone"
              autoComplete="tel"
              inputMode="numeric"
              value={formData.user_phone}
              onChange={handleForm}
              className={inputClassName}
              placeholder="10-digit number"
              pattern="[0-9]{10}"
              required
              title="Please enter a valid 10-digit phone number (no spaces or dashes)"
            />
          </label>
        </div>

        <label htmlFor="email" className="block text-xs font-semibold text-slate-700 sm:text-sm">
          Work email <span className="text-rose-500">*</span>
          <input
            type="email"
            name="user_email"
            id="email"
            autoComplete="email"
            value={formData.user_email}
            onChange={handleForm}
            className={inputClassName}
            placeholder="you@company.com"
            required
          />
        </label>

        <label htmlFor="project-details" className="block text-xs font-semibold text-slate-700 sm:text-sm">
          What can we help you build? <span className="text-rose-500">*</span>
          <textarea
            name="user_project"
            id="project-details"
            value={formData.user_project}
            onChange={handleForm}
            className={`${inputClassName} quote-form-message min-h-[64px] resize-none sm:min-h-[76px]`}
            rows={2}
            placeholder="Tell us about your project, goals and timeline..."
            required
          />
        </label>

        <button
          className="quote-form-submit group flex min-h-[46px] w-full items-center justify-center gap-3 rounded-full border border-[#e5c300] bg-[#FFDB16] px-5 text-sm font-extrabold text-slate-950 shadow-[0_8px_20px_rgba(255,219,22,0.2)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#ffe13d] hover:shadow-[0_12px_26px_rgba(255,219,22,0.3)] disabled:cursor-wait disabled:opacity-70 disabled:hover:translate-y-0 sm:min-h-[48px] sm:text-base"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <RotatingLines
                strokeColor="currentColor"
                strokeWidth="5"
                animationDuration="0.75"
                width="22"
                visible
              />
              Sending your request…
            </>
          ) : (
            <>
              Get my free consultation
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-950 text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <FiArrowUpRight className="text-lg" aria-hidden="true" />
              </span>
            </>
          )}
        </button>
      </form>

    </div>
  );
}

export default Form;
